import { useNavigate } from 'react-router-dom';
import { useApprovalHistory } from '@saiens/shared/hooks';
import { formatWorkHours } from '@saiens/shared/utils';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ArrowLeft, CheckCircle, XCircle } from 'lucide-react';
import type { ReviewStatus } from '@saiens/shared/types';

function StatusBadge({ status }: { status: ReviewStatus }) {
  if (status === 'approved') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
        <CheckCircle className="h-3 w-3" />
        已核准
      </span>
    );
  }
  if (status === 'rejected') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
        <XCircle className="h-3 w-3" />
        已駁回
      </span>
    );
  }
  return <span className="text-xs text-gray-400">{status}</span>;
}

export function ApprovalHistoryPage() {
  const navigate = useNavigate();
  const { data: historyData, isLoading } = useApprovalHistory({ limit: 50 });

  return (
    <div className="mx-auto max-w-lg">
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center gap-3 bg-white px-4 py-3 shadow-sm">
        <button onClick={() => navigate(-1)} className="rounded-full p-1 hover:bg-gray-100">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-lg font-semibold">簽核歷史</h1>
      </div>

      <div className="px-4 py-4">
        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : historyData && historyData.items.length > 0 ? (
          <div className="space-y-2">
            {historyData.items.map((review) => {
              const rd = review.resource_data;
              return (
                <button
                  key={review.id}
                  onClick={() => navigate(`/approval/${review.id}`)}
                  className="flex w-full items-center justify-between rounded-xl bg-white p-4 shadow-sm transition-colors hover:bg-gray-50"
                >
                  <div className="text-left">
                    <div className="mb-1 flex items-center gap-2">
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
                        {review.model_display}
                      </span>
                      <StatusBadge status={review.status} />
                    </div>
                    <p className="text-sm font-medium text-gray-900">
                      {'employee_name' in rd ? rd.employee_name : review.resource_name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {rd.type === 'leave' && `${rd.holiday_type_name} | ${formatWorkHours(rd.holiday_time_total)}`}
                      {rd.type === 'overtime' && `${rd.overtime_type_name} | ${formatWorkHours(rd.duration_time)}`}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-400">{review.reviewed_date || review.create_date}</p>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-gray-400">暫無簽核歷史</p>
        )}
      </div>
    </div>
  );
}
